
import { findAmount, standardizeUnit, formatFraction } from '../recipeFormatters';

// Dessert detection utility, internal use only.
const isDessertRecipe = (ingredients: string[]): boolean => {
  const dessertIndicators = [
    'sugar', 'chocolate', 'vanilla', 'honey', 'maple syrup',
    'cookie', 'cake', 'brownie', 'pastry', 'pie', 'custard',
    'pudding', 'frosting', 'icing', 'dessert'
  ];
  
  return ingredients.some(ingredient => 
    dessertIndicators.some(indicator =>
      ingredient.toLowerCase().includes(indicator)
    )
  );
};

// Check if an ingredient is present in the recipe
const hasIngredient = (ingredients: string[], keyword: string): boolean => {
  return ingredients.some(ingredient => 
    ingredient.toLowerCase().includes(keyword.toLowerCase())
  );
};

// Ingredient alternative definition (reuses structure from original file)
type IngredientAlternative = {
  original: string;
  alternative: string | null;
  note: string | null;
  ratio: number;
  keywords: string[];
  approxCount?: {
    unit: string;
    amount: number;
    note: string;
  };
};

export const generateHealthyAlternatives = (ingredients: string[]) => {
  const ingredientsLower = ingredients.map(i => i.toLowerCase());
  const isForDessert = isDessertRecipe(ingredients);
  
  // First, check what ingredients are actually present in the recipe
  const hasButter = hasIngredient(ingredients, 'butter');
  const hasSugar = hasIngredient(ingredients, 'sugar');
  const hasWhiteFlour = hasIngredient(ingredients, 'flour');
  const hasOil = hasIngredient(ingredients, 'oil');
  const hasCream = hasIngredient(ingredients, 'cream');
  const hasSalt = hasIngredient(ingredients, 'salt');
  const hasGroundBeef = hasIngredient(ingredients, 'ground beef');
  const hasBacon = hasIngredient(ingredients, 'bacon');
  const hasSourCream = hasIngredient(ingredients, 'sour cream');
  
  const alternatives: Record<string, IngredientAlternative> = {};
  
  // Only add alternatives for ingredients that are actually in the recipe
  
  // Baking Alternatives
  if (hasButter) {
    alternatives['butter'] = {
      original: "butter",
      alternative: "applesauce",
      note: "Use unsweetened applesauce for a healthier option",
      ratio: 0.5,
      keywords: ['applesauce'],
      approxCount: {
        unit: "banana",
        amount: 1,
        note: "1 medium banana equals about 1/2 cup mashed"
      }
    };
  }
  
  if (hasSugar) {
    alternatives['sugar'] = {
      original: "sugar",
      alternative: "mashed banana",
      note: "Use very ripe bananas for best results",
      ratio: 1,
      keywords: ['banana', 'bananas'],
      approxCount: {
        unit: "banana",
        amount: 1,
        note: "1 medium banana equals about 1/2 cup mashed"
      }
    };
  }
  
  if (hasWhiteFlour) {
    alternatives['white flour'] = {
      original: "white flour",
      alternative: "whole wheat flour",
      note: "Whole wheat flour adds more fiber and nutrients",
      ratio: 0.75,
      keywords: ['whole wheat', 'wheat flour']
    };
  }
  
  // Cooking Alternatives
  if (hasOil) {
    alternatives['oil'] = {
      original: "oil",
      alternative: "vegetable broth",
      note: "For sautéing, try vegetable broth for a lighter option",
      ratio: 1,
      keywords: ['broth', 'stock']
    };
  }
  
  if (hasCream) {
    alternatives['cream'] = {
      original: "cream",
      alternative: "coconut milk",
      note: "Coconut milk makes things creamy and adds natural sweetness",
      ratio: 1,
      keywords: ['coconut milk', 'coconut']
    };
  }
  
  if (hasSalt) {
    alternatives['salt'] = {
      original: "salt",
      alternative: isForDessert ? null : "herbs and spices",
      note: isForDessert ? null : "Fresh herbs and spices can add flavor without extra salt",
      ratio: 1,
      keywords: ['herbs', 'spices']
    };
  }
  
  // More cooking alternatives
  if (hasGroundBeef) {
    alternatives['ground beef'] = {
      original: "ground beef",
      alternative: "lentils",
      note: "Cooked lentils are a protein-rich vegetarian alternative",
      ratio: 1,
      keywords: ['lentil', 'lentils']
    };
  }
  
  if (hasBacon) {
    alternatives['bacon'] = {
      original: "bacon",
      alternative: "smoked paprika",
      note: "A pinch of smoked paprika can add a smoky flavor without the meat",
      ratio: 0.25,
      keywords: ['paprika', 'smoked']
    };
  }
  
  if (hasSourCream) {
    alternatives['sour cream'] = {
      original: "sour cream",
      alternative: "plain yogurt",
      note: "Greek yogurt is a protein-rich alternative that's still creamy",
      ratio: 1,
      keywords: ['yogurt', 'greek yogurt']
    };
  }

  return ingredients.flatMap(ingredient => {
    const lowerIngredient = ingredient.toLowerCase();
    const matchedAlt = Object.entries(alternatives).find(([key, alt]) => {
      if (!lowerIngredient.includes(key)) return false;
      if (alt.alternative === null) return false; // Skip if no alternative is available
      const hasConflictingIngredient = alt.keywords.some(keyword =>
        ingredientsLower.some(ing => ing.includes(keyword))
      );
      return !hasConflictingIngredient;
    });

    if (matchedAlt) {
      const [_, altInfo] = matchedAlt;
      const amount = findAmount(ingredient);

      if (amount) {
        const newAmount = Number(amount.amount) * altInfo.ratio;
        const unit = standardizeUnit(amount.unit);

        let alternativeText = `${formatFraction(newAmount)} ${unit} ${altInfo.alternative}`;

        if (altInfo.approxCount && altInfo.alternative && altInfo.alternative.includes(altInfo.approxCount.unit)) {
          const count = Math.ceil((newAmount * 2) * altInfo.approxCount.amount);
          if (count > 0) {
            alternativeText = `${count} ${count > 1 ? altInfo.approxCount.unit + 's' : altInfo.approxCount.unit} (mashed)`;
          }
        }
        return [{
          original: `${amount.originalAmount} ${unit} ${altInfo.original}`,
          alternative: alternativeText,
          note: altInfo.note,
        }];
      }
      return [{
        original: altInfo.original,
        alternative: altInfo.alternative,
        note: altInfo.note,
      }];
    }
    return [];
  });
};
